import { Icon } from '@iconify/react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import {
  ConversationRatingConfigDto,
  UpdateConversationRatingConfigDto,
} from '@/Domain/api/WIdget/dto/ConversationRatingConfig.dto';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { ConversationRatingGraphic } from '@/Domain/components/Widget/ConversationRating/ConversationRating.graphic';

type Props = {
  configuration?: ConversationRatingConfigDto;
};

export const ConversationRatingHeader = ({ configuration }: Props) => {
  const { t } = useTranslation('website', { keyPrefix: 'conversation_rating' });

  const updateConversationRatingConfigurationMutation = useMutation<
    ConversationRatingConfigDto,
    unknown,
    UpdateConversationRatingConfigDto
  >({
    mutationKey: [QueryKey.conversationRatingConfiguration.patch],
  });
  const onCheckedChange = (checked: boolean) => {
    updateConversationRatingConfigurationMutation.mutate({
      conv_rating_active: checked,
      conv_rating_popup_active: checked,
    });
  };

  return (
    <div className="rounded-xl bg-muted" data-testid="conversation-rating-header">
      <div className="min-h-[166px] grid grid-cols-[1fr_auto] gap-20">
        <div className="py-6 pl-6 pr-6 xl:pr-0 flex flex-col justify-between gap-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{t('title')}</h3>
            <Switch
              data-testid="conversation-rating-activation-button"
              checked={configuration?.conv_rating_active}
              onCheckedChange={onCheckedChange}
            />
          </div>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
          <a
            href="https://www.quickchat.ai/post/feature-announcement-conversation-rating"
            className="text-sm font-medium text-foreground flex items-center gap-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('learn_more')}
            <Icon icon="mingcute:arrow-right-up-line" />
          </a>
        </div>
        <section className="hidden xl:block mt-auto">
          <ConversationRatingGraphic />
        </section>
      </div>
    </div>
  );
};
